import { Box, Button, Card, Typography, Chip, Stack, Divider, CircularProgress, Tooltip } from '@mui/material'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import WTypography from 'Components/whiteTypography'
import AddTaskIcon from '@mui/icons-material/AddTask'
import CloseIcon from '@mui/icons-material/Close'
import QuestionMarkIcon from '@mui/icons-material/QuestionMark'
import LockIcon from '@mui/icons-material/Lock'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { makeGetRequest, makePostRequest } from 'utils/api'
import SuggestionForm from './SuggestionForm'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next' // LAP-733 (GE022)

function SuggestionDeTaches({ oldData, updateReports }) {
  const { t } = useTranslation('layout') // LAP-733 (GE022)
  let w_tpriority = localStorage.getItem('w-tpriority')
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()
  const siteUser = useSelector((store) => store.user)
  const [suggestions, setSuggestions] = useState([])
  const [priorities, setPriorities] = useState([])
  const [loading, setLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState({})

  useEffect(() => {
    if (w_tpriority) {
      setOpen(true)
    }
  }, [])

  useEffect(() => {
    fetchSuggestions()
    fetchPriorities()
  }, [])

  const fetchSuggestions = async () => {
    setLoading(true)
    try {
      const { data, status } = await makeGetRequest('/api/general/task-suggestions/', {}, true)
      if (status === 200) {
        setSuggestions(data)
      }
    } catch (error) {
      console.error('Error fetching suggestions:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchPriorities = async () => {
    try {
      const { data, status } = await makeGetRequest('/api/general/userpriority/', {}, true)
      if (status === 200) {
        // Get top 3 priorities
        setPriorities(data.slice(0, 3))
      }
    } catch (error) {
      console.error('Error fetching priorities:', error)
    }
  }

  const handleAccept = async (suggestionId) => {
    setActionLoading(prev => ({ ...prev, [suggestionId]: 'accept' }))
    try {
      const response = await makePostRequest(
        `/api/general/task-suggestions/${suggestionId}/accept/`,
        {},
        {},
        true,
      )
      if (response.status === 200 && response.data.suggestions) {
        // Backend generates 1 new suggestion and returns updated list of 3
        // No need to refetch - just update state with response
        setSuggestions(response.data.suggestions)
        
        // If backend didn't return exactly 3, fetch to ensure we have 3
        if (response.data.suggestions.length < 3) {
          await fetchSuggestions()
        }
        
        updateReports()
      }
    } catch (error) {
      console.error('Error accepting suggestion:', error)
      // On error, refetch to ensure consistent state
      await fetchSuggestions()
    } finally {
      setActionLoading(prev => {
        const newState = { ...prev }
        delete newState[suggestionId]
        return newState
      })
    }
  }

  const handleDismiss = async (suggestionId) => {
    setActionLoading(prev => ({ ...prev, [suggestionId]: 'dismiss' }))
    try {
      const response = await makePostRequest(
        `/api/general/task-suggestions/${suggestionId}/dismiss/`,
        {},
        {},
        true,
      )
      if (response.status === 200 && response.data.suggestions) {
        // Backend generates 1 new suggestion and returns updated list of 3
        // No need to refetch - just update state with response
        setSuggestions(response.data.suggestions)
        
        // If backend didn't return exactly 3, fetch to ensure we have 3
        if (response.data.suggestions.length < 3) {
          await fetchSuggestions()
        }
        
        updateReports()
      }
    } catch (error) {
      console.error('Error dismissing suggestion:', error)
      // On error, refetch to ensure consistent state
      await fetchSuggestions()
    } finally {
      setActionLoading(prev => {
        const newState = { ...prev }
        delete newState[suggestionId]
        return newState
      })
    }
  }

  const formatTimeWindow = (start, end) => {
    if (!start || !end) return { date: '', time: '' }
    try {
      const startDate = new Date(start)
      const endDate = new Date(end)
      
      // LAP-733 (GE022): localised weekday/month names
      const days = [
        t('syntheseTemps.dayLongSun'),
        t('syntheseTemps.dayLongMon'),
        t('syntheseTemps.dayLongTue'),
        t('syntheseTemps.dayLongWed'),
        t('syntheseTemps.dayLongThu'),
        t('syntheseTemps.dayLongFri'),
        t('syntheseTemps.dayLongSat'),
      ]
      const months = [
        t('syntheseTemps.monthLongJan'),
        t('syntheseTemps.monthLongFeb'),
        t('syntheseTemps.monthLongMar'),
        t('syntheseTemps.monthLongApr'),
        t('syntheseTemps.monthLongMay'),
        t('syntheseTemps.monthLongJun'),
        t('syntheseTemps.monthLongJul'),
        t('syntheseTemps.monthLongAug'),
        t('syntheseTemps.monthLongSep'),
        t('syntheseTemps.monthLongOct'),
        t('syntheseTemps.monthLongNov'),
        t('syntheseTemps.monthLongDec'),
      ]
      
      const dayName = days[startDate.getDay()]
      const day = startDate.getDate()
      const monthName = months[startDate.getMonth()]
      
      const dateStr = `${dayName} ${day} ${monthName}`
      
      // Format time
      const startHours = String(startDate.getHours()).padStart(2, '0')
      const startMinutes = String(startDate.getMinutes()).padStart(2, '0')
      const endHours = String(endDate.getHours()).padStart(2, '0')
      const endMinutes = String(endDate.getMinutes()).padStart(2, '0')
      
      const timeStr = `${startHours}:${startMinutes} - ${endHours}:${endMinutes}`
      
      return {
        date: dateStr,
        time: timeStr
      }
    } catch (error) {
      return { date: '', time: '' }
    }
  }

  const getDomainColor = (domainCode) => {
    const colors = {
      'Professionnel': '#4a90e2',
      'Finance': '#50c878',
      'Temps': '#f39c12',
      'Personnel': '#9b59b6',
      'Social': '#e74c3c',
      'Famille': '#1abc9c',
      'Administratif': '#95a5a6',
      'Earth': '#27ae60',
    }
    return colors[domainCode] || '#7f8c8d'
  }

  if (!siteUser?.user?.has_subscription) {
    return (
      
      <Card className="dashboard_card__root1">
        <Box
          sx={{
            padding: 4,
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography
            variant="h5"
            component="h1"
            sx={{
              color: '#e0e0e0',
              fontWeight: 600,
              marginBottom: 4,
            }}
          >
            {t('syntheseTemps.suggestionsTitle')}
          </Typography>

          <LockIcon
            sx={{
              fontSize: 80,
              color: '#5a5a6b',
              marginBottom: 4,
            }}
          />

          <Typography
            variant="h6"
            sx={{
              color: '#e0e0e0',
              marginBottom: 6,
              lineHeight: 1.5,
            }}
          >
            {t('syntheseTemps.suggestionsLockedSubtitle')}
          </Typography>

          <Button
            variant="contained"
            className="no-drag"
            sx={{
              width: '100%',
              backgroundColor: '#38384d',
              color: '#e0e0e0',
              padding: '12px 0',
              fontSize: '1rem',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#4a4a5f',
              },
            }}
            onClick={() => {
              navigate('/subscription')
            }}
          >
            {t('syntheseTemps.goToPremium')}
          </Button>
        </Box>
      </Card>
    )
  }

  if (suggestions.length === 0 && !loading) {
    return (
      <Card className="dashboard_card__root1">
          <Box className="dashboard_card__header1">
            <WTypography component={'span'} variant="h6">
              {t('syntheseTemps.suggestionsTitle')}
            </WTypography>
          </Box>



        <Box className="dashboard_card__header1">
          <WTypography sx={{ color: '#ffffffa1' }} variant={'body2'}>
            {t('syntheseTemps.suggestionsSubtitle')}
          </WTypography>
        </Box>

        <Box mt={3} minWidth={3} mb={6} height={'100%'}>
          <Box
            className="no-drag"
            height={'70%'}
            justifyContent={'center'}
            color={'white'}
            display={'flex'}
            flexDirection={'column'}
            alignItems={'center'}
          >
            <SuggestionForm
              open={open}
              setOpen={setOpen}
              startIcon={
                <Box color={'#00295f'} padding={5} bgcolor={'white'} borderRadius={'50%'} mb={3}>
                  <QuestionMarkIcon sx={{ fontSize: '55px' }} />
                </Box>
              }
              updateReports={async () => {
                // Show loading state while generating suggestions
                setLoading(true)
                setSuggestions([])
                
                try {
                  await fetchSuggestions()
                  await fetchPriorities()
                  updateReports()
                } finally {
                  setLoading(false)
                }
              }}
            />

            <Box padding={'0 20px'}>{t('syntheseTemps.prioritiesQuestionPrompt')}</Box>
          </Box>
        </Box>
      </Card>
    )
  }

  return (
    <Card className="dashboard_card__root1" sx={{ overflow: 'visible', display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <Box className="dashboard_card__header1" sx={{marginBottom: '10px'}}>
        <WTypography component={'span'} variant="h6">
          {t('syntheseTemps.suggestionsTitle')}
        </WTypography>
      </Box>

      {/* Loading State - Full Card */}
      {loading ? (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          flex={1}
          py={4}
        >
          <CircularProgress sx={{ color: 'white', mb: 2 }} size={40} />
          <Typography sx={{ color: '#b0b0c0', fontSize: '0.875rem' }}>
            {t('syntheseTemps.generatingSuggestions')}
          </Typography>
        </Box>
      ) : (
        <>
          {/* Top Priorities Section */}
          {priorities.length > 0 && (
        <Box px={2} pt={1.5} pb={0.5}>
          <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
            <TrendingUpIcon sx={{ color: '#ffd700', fontSize: 14 }} />
            <Typography variant="caption" sx={{ color: '#ffffffa1', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem' }}>
              {t('syntheseTemps.yourPriorities')}
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {priorities.map((priority, index) => (
              <Chip
                key={priority.id}
                label={`${index + 1}. ${priority.domain_code}`}
                size="small"
                sx={{
                  backgroundColor: getDomainColor(priority.domain_code),
                  color: 'white',
                  fontWeight: 500,
                  fontSize: '0.65rem',
                  height: '20px',
                  border: index === 0 ? '1.5px solid #ffd700' : 'none',
                  boxShadow: index === 0 ? '0 0 6px rgba(255, 215, 0, 0.3)' : 'none',
                  '& .MuiChip-label': {
                    px: 0.75,
                    py: 0,
                  }
                }}
              />
            ))}
          </Stack>
        </Box>
      )}

      <Divider sx={{ borderColor: '#3a3a4e', my: 0.5 }} />

      {/* Suggestions Section */}
      <Box 
        px={1.5} 
        pb={1.5} 
        position="relative"
        sx={{
          '&::-webkit-scrollbar-track': {
            backgroundColor: '#2a2a3e',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#4a4a5f',
            borderRadius: '3px',
            '&:hover': {
              backgroundColor: '#5a5a6f',
            },
          },
        }}
      >
        {loading && (
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            display="flex"
            alignItems="center"
            justifyContent="center"
            bgcolor="rgba(42, 42, 62, 0.8)"
            zIndex={10}
            borderRadius={2}
          >
            <CircularProgress sx={{ color: 'white' }} />
          </Box>
        )}
        
        {!loading && suggestions.length === 0 ? (
          <Box display="flex" justifyContent="center" py={2}>
            <Typography sx={{ color: '#9a9aaa', fontSize: '0.85rem' }}>{t('syntheseTemps.noSuggestionsAvailable')}</Typography>
          </Box>
        ) : (
          <Stack spacing={1}>
            {suggestions.map((suggestion, index) => {
              const timeWindow = formatTimeWindow(
                suggestion.suggested_time_window_start,
                suggestion.suggested_time_window_end,
              )
              
              return (
                <Box
                  key={suggestion.id}
                  className="mb-3"
                  sx={{
                    backgroundColor: '#2a2a3e',
                    borderRadius: 1.5,
                    padding: 1.25,
                    border: '1px solid #3a3a4e',
                    position: 'relative',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: getDomainColor(suggestion.domain_code),
                      boxShadow: `0 2px 8px rgba(0, 0, 0, 0.3)`,
                    },
                  }}
                >
                  {/* Domain Badge - Top Left */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -6,
                      left: 10,
                      backgroundColor: getDomainColor(suggestion.domain_code),
                      color: 'white',
                      px: 1,
                      py: 0.25,
                      borderRadius: 0.75,
                      fontSize: '0.6rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: 0.3,
                      lineHeight: 1.2,
                    }}
                  >
                    {suggestion.domain_code}
                  </Box>

                  {/* Title */}
                  <Typography
                    variant="h6"
                    sx={{
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      mt: 1.5,
                      mb: 0.75,
                      lineHeight: 1.2,
                    }}
                  >
                    {suggestion.name}
                  </Typography>

                  {/* Time Info */}
                  <Stack spacing={0.5} mb={1} direction={'row'}>
                    {timeWindow.date && (
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <CalendarTodayIcon sx={{ color: '#8a8a9a', fontSize: 12 }} />
                        <Typography variant="body2" sx={{ color: '#b0b0c0', fontSize: '0.7rem', lineHeight: 1 }}>
                          {timeWindow.date}
                        </Typography>
                      </Box>
                    )}
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <AccessTimeIcon sx={{ color: '#8a8a9a', fontSize: 12 }} />
                      <Typography variant="body2" sx={{ color: '#b0b0c0', fontSize: '0.7rem', lineHeight: 1 }}>
                        {timeWindow.time || `${suggestion.estimated_minutes} minutes`}
                      </Typography>
                    </Box>
                  </Stack>

                  {/* Action Buttons - Bottom */}
                  <Stack direction="row" spacing={0.5} className="no-drag" justifyContent="flex-end">
                    <Tooltip 
                      title={
                        <Box sx={{ p: 0.5 }}>
                          <Typography variant="body2" sx={{ fontSize: '0.75rem', lineHeight: 1.4 }}>
                            {suggestion.reason || t('syntheseTemps.noDescriptionAvailable')}
                          </Typography>
                        </Box>
                      }
                      arrow
                      placement="top"
                    >
                      <Box
                        sx={{
                          backgroundColor: '#5a5a6f',
                          color: 'white',
                          px: 0.75,
                          py: 0.25,
                          borderRadius: 0.75,
                          fontSize: '0.6rem',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: 0.3,
                          lineHeight: 1.2,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            backgroundColor: '#6a6a7f',
                            transform: 'scale(1.05)',
                          },
                        }}
                      >
                        <VisibilityIcon sx={{ fontSize: 10 }} />
                      </Box>
                    </Tooltip>

                    <Box
                      onClick={() => !actionLoading[suggestion.id] && handleAccept(suggestion.id)}
                      sx={{
                        backgroundColor: actionLoading[suggestion.id] === 'accept' ? '#45a049' : '#4caf50',
                        color: 'white',
                        px: 1,
                        py: 0.25,
                        borderRadius: 0.75,
                        fontSize: '0.6rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.3,
                        lineHeight: 1.2,
                        cursor: actionLoading[suggestion.id] ? 'not-allowed' : 'pointer',
                        opacity: actionLoading[suggestion.id] && actionLoading[suggestion.id] !== 'accept' ? 0.5 : 1,
                        pointerEvents: actionLoading[suggestion.id] && actionLoading[suggestion.id] !== 'accept' ? 'none' : 'auto',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: '#45a049',
                          transform: 'scale(1.05)',
                        },
                      }}
                    >
                      {actionLoading[suggestion.id] === 'accept' ? (
                        <>
                          <CircularProgress size={10} sx={{ color: 'white' }} />
                          <span>{t('syntheseTemps.addingLabel')}</span>
                        </>
                      ) : (
                        <>
                          <AddTaskIcon sx={{ fontSize: 10 }} />
                          <span>{t('syntheseTemps.addLabel')}</span>
                        </>
                      )}
                    </Box>
                    
                    <Box
                      onClick={() => !actionLoading[suggestion.id] && handleDismiss(suggestion.id)}
                      sx={{
                        backgroundColor: actionLoading[suggestion.id] === 'dismiss' ? '#d32f2f' : '#f44336',
                        color: 'white',
                        px: 1,
                        py: 0.25,
                        borderRadius: 0.75,
                        fontSize: '0.6rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.3,
                        lineHeight: 1.2,
                        cursor: actionLoading[suggestion.id] ? 'not-allowed' : 'pointer',
                        opacity: actionLoading[suggestion.id] && actionLoading[suggestion.id] !== 'dismiss' ? 0.5 : 1,
                        pointerEvents: actionLoading[suggestion.id] && actionLoading[suggestion.id] !== 'dismiss' ? 'none' : 'auto',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: '#d32f2f',
                          transform: 'scale(1.05)',
                        },
                      }}
                    >
                      {actionLoading[suggestion.id] === 'dismiss' ? (
                        <>
                          <CircularProgress size={10} sx={{ color: 'white' }} />
                          <span>{t('syntheseTemps.dismissingLabel')}</span>
                        </>
                      ) : (
                        <>
                          <CloseIcon sx={{ fontSize: 10 }} />
                          <span>{t('syntheseTemps.dismissLabel')}</span>
                        </>
                      )}
                    </Box>
                  </Stack>
                </Box>
              )
            })}
          </Stack>
        )}
      </Box>
        </>
      )}
    </Card>
  )
}

export default SuggestionDeTaches
